import { useGiftCardRequest } from '../hooks/use-gift-card-request';
import { generateKeyPair, decrypt } from '../utils/crypto';
import { IEncryptedPayload, IGiftCardCommonData } from '../models/common-models';

interface IBarcodeRequest {
  paymentIds?: string[];
  otpVerificationInput?: {
    email: string;
    encryptedPayload: string;
    otp: string;
  };
  giftCardCipherKeyInput: {
    x: string;
    y: string;
  };
}

interface IDecryptedGiftCard {
  id: string;
  encryptedGiftCard: IEncryptedPayload;
}

interface GiftCardBarcodeResult {
  success: boolean;
  barcodeData?: Partial<IGiftCardCommonData>[];
  error?: string;
  // OTP verification result fields
  otpVerified?: boolean;
  remainingCount?: string;
  errorCode?: string;
}

export const useGiftCardBarcodeService = () => {
  const { makeRequest: getCards } = useGiftCardRequest({
    queryName: 'getGiftCardBarcode'
  });

  const getGiftCardBarcodes = async (
    paymentIds: string[],
    otpVerificationInput?: {
      email: string;
      encryptedPayload: string;
      otp: string;
    }
  ): Promise<GiftCardBarcodeResult> => {
    try {
      // Generate encryption keys for the barcode request
      const keyPair = await generateKeyPair();

      if (!keyPair) {
        return {
          success: false,
          error: 'Failed to generate encryption keys'
        };
      }

      const { publicJwk, privateJwk } = keyPair;

      // Prepare the request for getGiftCardBarcode
      const variables: IBarcodeRequest = {
        paymentIds,
        giftCardCipherKeyInput: {
          // eslint-disable-next-line id-length
          x: publicJwk.x || '',
          // eslint-disable-next-line id-length
          y: publicJwk.y || ''
        }
      };

      // Add OTP verification input if provided
      if (otpVerificationInput) {
        variables.otpVerificationInput = otpVerificationInput;
      }

      // Call the getGiftCardBarcode GraphQL endpoint
      const data = await getCards(variables);
      const giftCards = data?.paymentCards;
      const otpVerificationResult = data?.otpVerificationResult;

      // Check OTP verification result if OTP input was provided
      if (otpVerificationInput && otpVerificationResult) {
        const otpVerified = otpVerificationResult.otpVerified === 'true';

        // If OTP verification failed, return the verification status
        if (!otpVerified) {
          return {
            success: false,
            otpVerified: false,
            remainingCount: otpVerificationResult.remainingCount,
            errorCode: otpVerificationResult.errorCode,
            error: otpVerificationResult.errorMessage || 'Invalid verification code'
          };
        }
      }

      if (giftCards?.length > 0) {
        // Decrypt the gift card data
        const barcodeData: Partial<IGiftCardCommonData>[] = [];
        await Promise.all(
          giftCards.map(async (card: IDecryptedGiftCard) => {
            const result = await decrypt(privateJwk as unknown as CryptoKey, card?.encryptedGiftCard);
            barcodeData.push({
              ...result,
              id: card.id
            });
          })
        );

        return {
          success: true,
          barcodeData,
          otpVerified: true
        };
      }

      return {
        success: false,
        error: 'No gift card data received'
      };
    } catch (error) {
      // Check if the error has the specific CUST_AUTH_ERR_119 error code
      const errorCode = (error as { errorCode?: string })?.errorCode;
      const errorMessage = (error as { errorMessage?: string })?.errorMessage;

      if (errorCode === 'CUST_AUTH_ERR_119' && errorMessage === 'OTP Verification Failed') {
        return {
          success: false,
          otpVerified: false,
          errorCode: 'CUST_AUTH_ERR_119',
          error: 'OTP Verification Failed'
        };
      }

      return {
        success: false,
        error: 'Failed to retrieve gift card data'
      };
    }
  };

  return {
    getGiftCardBarcodes
  };
};
